import { supabase } from '../lib/supabase';

class AgentManagementService {
  // Get all agents for the current user
  async getAllAgents() {
    try {
      const { data, error } = await supabase
        ?.from('agents')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        throw new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.');
      }
      throw error;
    }
  }

  // Create a new agent
  async createAgent(agentData) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Validate required fields
      if (!agentData?.name?.trim()) {
        throw new Error('Agent name is required');
      }

      const { data, error } = await supabase
        ?.from('agents')
        ?.insert({
          ...agentData,
          user_id: user?.id,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        throw new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.');
      }
      throw error;
    }
  }

  // Update agent information
  async updateAgent(agentId, agentData) {
    try {
      // Validate required fields
      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      if (!agentData?.name?.trim()) {
        throw new Error('Agent name is required');
      }

      const { data, error } = await supabase
        ?.from('agents')
        ?.update({
          ...agentData,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', agentId)
        ?.select()
        ?.single();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        throw new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.');
      }
      throw error;
    }
  }

  // Delete an agent
  async deleteAgent(agentId) {
    try {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      const { error } = await supabase
        ?.from('agents')
        ?.delete()
        ?.eq('id', agentId);

      if (error) {
        throw new Error(error.message);
      }

      return true;
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        throw new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.');
      }
      throw error;
    }
  }

  // Get agent by ID
  async getAgentById(agentId) {
    try {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      const { data, error } = await supabase
        ?.from('agents')
        ?.select('*')
        ?.eq('id', agentId)
        ?.single();

      if (error) {
        if (error?.code === 'PGRST116') {
          throw new Error('Agent not found');
        }
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        throw new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.');
      }
      throw error;
    }
  }

  // Get agent by ID for public access (embed widgets)
  async getPublicAgentById(agentId) {
    try {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      // Use RPC function call to bypass RLS for public agent access
      const { data, error } = await supabase
        ?.rpc('get_public_agent', { agent_uuid: agentId });

      if (error) {
        throw new Error(error.message);
      }

      if (!data || data?.length === 0) {
        throw new Error('Agent not found');
      }

      const agentData = data?.[0];

      // Add computed properties for public access
      return {
        ...agentData,
        tagline: this.generateTagline(agentData),
        welcome_message: this.generateWelcomeMessage(agentData)
      };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        throw new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.');
      }
      throw error;
    }
  }

  // Generate a tagline based on agent data
  generateTagline(agentData) {
    if (!agentData) return 'How can I help you today?';
    
    // Check if there's a custom tagline in appearance
    if (agentData?.appearance?.tagline) {
      return agentData?.appearance?.tagline;
    }
    
    // Generate based on agent type or system prompt
    const systemPrompt = agentData?.system_prompt?.toLowerCase() || '';
    
    if (systemPrompt?.includes('customer') || systemPrompt?.includes('support')) {
      return 'How can I assist you today?';
    } else if (systemPrompt?.includes('sales') || systemPrompt?.includes('marketing')) {
      return 'Let me help you find what you need!';
    } else if (systemPrompt?.includes('technical') || systemPrompt?.includes('developer')) {
      return 'Ready to solve your technical challenges!';
    } else if (systemPrompt?.includes('creative') || systemPrompt?.includes('design')) {
      return 'Let\'s create something amazing together!';
    }
    
    return 'How can I help you today?';
  }

  // Generate a welcome message based on agent data
  generateWelcomeMessage(agentData) {
    if (!agentData) return 'Hello! I\'m here to help. What can I do for you today?';
    
    // Check if there's a custom welcome message in initial_messages
    if (agentData?.initial_messages && agentData?.initial_messages?.length > 0) {
      return agentData?.initial_messages?.[0];
    }
    
    // Check if there's a custom welcome message in appearance
    if (agentData?.appearance?.welcome_message) {
      return agentData?.appearance?.welcome_message;
    }
    
    const displayName = agentData?.display_name || agentData?.name;
    
    if (displayName) {
      return `Hello! I'm ${displayName}. How can I assist you today?`;
    }
    
    return 'Hello! I\'m here to help. What can I do for you today?';
  }

  // Clone an agent
  async cloneAgent(agentId) {
    try {
      const originalAgent = await this.getAgentById(agentId);
      
      if (!originalAgent) {
        throw new Error('Agent not found');
      }

      // Remove fields that shouldn't be cloned
      const { id, created_at, updated_at, ...cloneData } = originalAgent;
      
      // Add clone suffix to name
      const clonedAgent = await this.createAgent({
        ...cloneData,
        name: `${cloneData?.name} (Copy)`,
        display_name: cloneData?.display_name ? `${cloneData?.display_name} (Copy)` : null
      });

      return clonedAgent;
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        throw new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.');
      }
      throw error;
    }
  }

  // Get agents with usage statistics
  async getAgentsWithStats() {
    try {
      const { data, error } = await supabase
        ?.from('agents')
        ?.select(`
          *,
          usage_logs(count)
        `)
        ?.order('created_at', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      // Transform data to include statistics
      const agentsWithStats = data?.map(agent => ({
        ...agent,
        chatCount: agent?.usage_logs?.length || 0,
        messageCount: Math.floor(Math.random() * 10000), // Placeholder until message tracking is implemented
        characterCount: Math.floor(Math.random() * 500000), // Placeholder until character tracking is implemented
        isActive: Boolean(agent?.created_at), // Simple active check
        isPinned: false, // Placeholder until pinning is implemented
        avatar: agent?.appearance?.avatar || null
      })) || [];

      return agentsWithStats;
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        throw new Error('Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.');
      }
      throw error;
    }
  }

  // Subscribe to real-time changes
  subscribeToChanges(callback) {
    const channel = supabase
      ?.channel('agents_changes')
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'agents' },
        (payload) => {
          callback?.(payload);
        }
      )
      ?.subscribe();

    return channel;
  }

  // Unsubscribe from real-time changes
  unsubscribeFromChanges(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
}

export const agentManagementService = new AgentManagementService();